import { Injectable } from '@angular/core';
import { Globals } from "../../shared/globals/globals";
import { UserService } from "./user.service";
import { Router } from "@angular/router";
import { UserModel } from "../../models/user/user.model";
import { WarehouseService } from "../warehouse/warehouse.service";
import { WarehouseModel } from "../../models/warehouse/warehouse.model";
import { TranslateService } from '@ngx-translate/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { PkceService } from './pkce.service';
import { environment } from 'src/environments/environment';
import { AUTH_CONSTANTS } from '../../shared/constants/auth.constants';
import { UserResourcesService } from '../resource/user-resources.service';

@Injectable()
export class LoginService {

	tokenExp: number;

	constructor(public globals: Globals
		  	  , private readonly router: Router
			  , private readonly userService: UserService
			  , private readonly warehouseService: WarehouseService
			  , private readonly translate: TranslateService
			  , private readonly http: HttpClient
			  , private readonly pkceService: PkceService
			  , private readonly userResources: UserResourcesService) {
				this.globals = globals;
				this.router = router;
				this.userService = userService;
				this.warehouseService = warehouseService;
				this.translate = translate;
				this.http = http;
				this.pkceService = pkceService;
			  }

	parseSessionStorage() {
		if (localStorage.length > 0) {
			this.tokenExp = localStorage['tokenExp'];
		}
	}

	isTokenExpired(): boolean {
		try {
			const encodedToken = localStorage['wingsEncodedAccessToken'];
			if (!encodedToken || encodedToken === 'null') {
				return true;
			}
			
			atob(encodedToken.split('.')[1]);
		} catch(e) {
			return true;
		}

		const epoch = Math.trunc(new Date().getTime() / 1000);
		if (!this.tokenExp) {
			return true; 
		}
		return (epoch >= this.tokenExp);
	}

	checkLogin(): boolean {		
		this.parseSessionStorage();
		if (this.isTokenExpired()) {
			this.clearSession();
			return false;
		}
		if (!this.globals.userLoaded()) {
			
			try {
				
				const encodedToken = localStorage['wingsEncodedAccessToken'];
				if (!encodedToken || encodedToken === 'null') {
					return false;
				}
				this.globals.tokenData = JSON.parse(atob(encodedToken.split('.')[1]));
				this.globals.user = new UserModel();
				this.globals.user.userName = this.globals.tokenData.uid;
				this.globals.user.userType = this.globals.tokenData.fordUserType;
				this.globals.user.warehouse.buildingCodeC = this.globals.tokenData.fordSiteCode;
				this.globals.user.country = this.globals.tokenData.c;
				this.translaterUser();

				this.userService.checkAccess(this.globals.user).subscribe({
					next: (hasAccess) => {
						if (hasAccess) {
							this.globals.user.hasAccess = true;
							this.userResources.setSession(this.globals.user.userName);
						} else {
							this.globals.user.hasAccess = false;
							this.router.navigate(['no-access']);
						}
					},
					error: (error: unknown) => {
						this.globals.user.hasAccess = false;
						if (UserService.isTransientError(error)) {
							this.router.navigate(['no-access'], { queryParams: { reason: 'connection' } });
						} else {
							this.router.navigate(['no-access']);
						}
					}
				});
			} catch (error) {
				console.error('Error loading user from token:', error);
				this.clearSession();
				return false;
			}
		}

		return true;
	}

	resolveWarehouse() {
		this.warehouseService.findByBuildingCodeC(Number(this.globals.tokenData.fordSiteCode)).subscribe({
			next: (data: WarehouseModel) => {
				this.globals.user.warehouse = data;
			},
			error: (err) => {
				console.log(err);
			}
		});
	}

	clearSession() {
		localStorage['strAccessToken'] = null;
		localStorage['wingsEncodedAccessToken'] = null;
		localStorage['tokenIssue'] = null;
		localStorage['tokenExp'] = null;
		localStorage['bkSubDivisionUidD'] = null;
		localStorage['authCode'] = null;
		localStorage['authState'] = null;

		this.pkceService.clearPkceValues();

		this.globals.tokenData = null;
	}

	exchangeCodeForToken(code: string): Observable<any> {
		const headers = new HttpHeaders({
			'Content-Type': 'application/x-www-form-urlencoded'
		});

		const codeVerifier = this.pkceService.getCodeVerifier();
		if (!codeVerifier) {
			console.error('No code_verifier found! PKCE flow may be broken.');
		}

		const body = new URLSearchParams({
			'client_id': environment.auth.client_id,
			'scope': environment.auth.scope,
			'code': code,
			'redirect_uri': environment.auth.redirect_uri,
			'grant_type': AUTH_CONSTANTS.GRANT_TYPE,
			'code_verifier': codeVerifier || ''
		}).toString();

		return this.http.post(environment.auth.url + AUTH_CONSTANTS.URL_PATH_TOKEN, body, { headers });
	}

	processTokenResponse(tokenResponse: any): void {
		
		if (tokenResponse.access_token) {
			try {
				localStorage.setItem('wingsEncodedAccessToken', tokenResponse.access_token);
				const idTokenPayload = atob(tokenResponse.access_token.split('.')[1]);
				const jsonToken = JSON.parse(idTokenPayload);
				
				localStorage.setItem('strAccessToken', idTokenPayload);
				localStorage.setItem('tokenIssue', jsonToken.iat);
				localStorage.setItem('tokenExp', jsonToken.exp);
				
			} catch (error) {
				console.error('Error parsing id_token:', error);
			}
		} else if (tokenResponse.access_token) {
			
			try {
				localStorage.setItem('wingsEncodedAccessToken', tokenResponse.access_token);
				const accessToken = atob(tokenResponse.access_token.split('.')[1]);
				const jsonToken = JSON.parse(accessToken);
				
				localStorage.setItem('strAccessToken', accessToken);
				localStorage.setItem('tokenIssue', jsonToken.iat);
				localStorage.setItem('tokenExp', jsonToken.exp);
			} catch (error) {
				console.log('Access token is not a JWT, storing as opaque token');
				localStorage.setItem('strAccessToken', tokenResponse.access_token);
				
				if (tokenResponse.expires_in) {
					const expirationTime = Math.floor(Date.now() / 1000) + tokenResponse.expires_in;
					localStorage.setItem('tokenExp', expirationTime.toString());
				}
			}
		}
		
		this.pkceService.clearPkceValues();
	}

	translaterUser() {

		switch (this.globals.user.country) {
			case 'BRA': {
				this.globals.user.language = 'pt-br';
				this.translate.use('pt');
				break;
			}
			case 'PER':
			case 'CHL':
			case 'ARG': {
				this.globals.user.language = 'es-ar';
				this.translate.use('es');
				break;
			}
			case 'COL': {
				this.globals.user.language = 'es-co';
				this.translate.use('es');
				break;
			}
			case 'VEN': {
				this.globals.user.language = 'es-ve';
				this.translate.use('es');
				break;
			}
			default: {
				this.globals.user.language = 'en-us';
				this.translate.use('en');
				break;
			}
		}
		this.translate.setDefaultLang('en');
	}
}
